import React from "react";
import {
  Button,
  CircularProgress,
  Grid,
  makeStyles,
  Typography,
} from "@material-ui/core";

import QuillEditor from "./QuillEditor";
import { TrackerNotesState } from "../TrackerNotes";

const useStyles = makeStyles((theme) => ({
  editorActions: {
    alignContent: "center",
    justifyContent: "right",
    marginTop: theme.spacing(3),
  },
  loaderWrapper: {
    position: "relative",
    width: "100%",
    minHeight: "100px",
  },
  loader: {
    left: "50%",
    position: "absolute",
    top: "50%",
    transform: "translateX(-50%) translateY(-50%)",
  },
  notesText: {
    backgroundColor: theme.palette.grey[50],
    padding: theme.spacing(2, 1),
  },
}));

export type TrackerNotesEmptyStateProps = {
  onClickAddNotes: () => void;
};

export function TrackerNotesLoadingState() {
  const classes = useStyles();

  return (
    <div className={classes.loaderWrapper}>
      <div className={classes.loader}>
        <CircularProgress />
      </div>
    </div>
  );
}

export function TrackerNotesEmptyState({
  onClickAddNotes,
}: TrackerNotesEmptyStateProps) {
  const classes = useStyles();

  return (
    <>
      <Typography component="p" variant="body1">
        Write notes like status updates, who owns this tracker, etc.
      </Typography>
      <Grid
        container
        spacing={1}
        className={classes.editorActions}
        justifyContent="space-between"
      >
        <Grid item>
          <Button
            variant="contained"
            color="primary"
            onClick={onClickAddNotes}
            size="small"
            title="Add Notes"
          >
            Add Notes
          </Button>
        </Grid>
      </Grid>
    </>
  );
}

export type TrackerNotesEditingStateProps = {
  content: string;
  onContentChanged: (content: string) => void;
  onClickCloseNotes: () => void;
  onClickSaveNotes: () => void;
};

export function TrackerNotesEditingState({
  content,
  onClickCloseNotes,
  onClickSaveNotes,
  onContentChanged,
}: TrackerNotesEditingStateProps) {
  const classes = useStyles();

  return (
    <>
      <QuillEditor content={content} onContentChanged={onContentChanged} />
      <Grid container spacing={1} className={classes.editorActions}>
        <Grid item>
          <Button
            variant="outlined"
            color="secondary"
            onClick={onClickCloseNotes}
            size="small"
            title="Cancel Editing Notes"
          >
            Cancel
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            color="primary"
            onClick={onClickSaveNotes}
            size="small"
            title="Save Notes"
          >
            Save
          </Button>
        </Grid>
      </Grid>
    </>
  );
}

export type TrackerNotesDisplayStateProps = {
  onClickEditNotes: () => void;
  note?: string;
};

export function TrackerNotesDisplayState({
  onClickEditNotes,
  note = "",
}: TrackerNotesDisplayStateProps) {
  const classes = useStyles();

  return (
    <>
      <div
        className={classes.notesText}
        dangerouslySetInnerHTML={{ __html: note }}
      />

      <Grid container spacing={1} className={classes.editorActions}>
        <Grid item>
          <Button
            variant="contained"
            color="primary"
            onClick={onClickEditNotes}
            size="small"
            title="Edit Notes"
          >
            Edit
          </Button>
        </Grid>
      </Grid>
    </>
  );
}

export type TrackerNotesContentProps = {
  content: string;
  onClickCloseNotes: () => void;
  onClickEditNotes: () => void;
  onClickSaveNotes: () => void;
  onContentChanged: (content: string) => void;
  trackerNotesState: TrackerNotesState;
};

function TrackerNotesContent({
  content,
  onClickCloseNotes,
  onClickEditNotes,
  onClickSaveNotes,
  onContentChanged,
  trackerNotesState,
}: TrackerNotesContentProps) {
  switch (trackerNotesState) {
    case TrackerNotesState.LOADING:
      return <TrackerNotesLoadingState />;

    case TrackerNotesState.EMPTY:
      return <TrackerNotesEmptyState onClickAddNotes={onClickEditNotes} />;

    case TrackerNotesState.EDITING:
      return (
        <TrackerNotesEditingState
          content={content}
          onContentChanged={onContentChanged}
          onClickCloseNotes={onClickCloseNotes}
          onClickSaveNotes={onClickSaveNotes}
        />
      );

    case TrackerNotesState.VIEWING:
      return (
        <TrackerNotesDisplayState
          note={content}
          onClickEditNotes={onClickEditNotes}
        />
      );
  }
}

export default TrackerNotesContent;
